import { Button } from "@/components/core/buttons/button";
import { useContractInvoicesListQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import contractInvoiceService from "@/features/dashboard/api/services/contract-invoice-service";
import type { ContractInvoice } from "@/features/dashboard/types/contract-invoice";
import { exportToXLSX } from "@/utils/export-data";
import { IconFileSpreadsheet } from "@tabler/icons-react";
import { endOfMonth, format, parse, startOfMonth } from "date-fns";
import type React from "react";
import { useState } from "react";

type InvoiceWithContract = ContractInvoice & {
	sales_contract?: {
		id: string;
		project?: { id: string; name: string };
	};
};

interface PendingInvoicesModalProps {
	month?: string; // e.g. "May 2025" — if omitted, uses dateFrom/dateTo range
	dateFrom?: string; // yyyy-MM-dd
	dateTo?: string; // yyyy-MM-dd
	salespersonId?: string;
}

export const PendingInvoicesModal: React.FC<PendingInvoicesModalProps> = ({ month, dateFrom, dateTo, salespersonId }) => {
	const [page, setPage] = useState(1);
	const [exporting, setExporting] = useState(false);

	let startDate: string;
	let endDate: string;
	let label: string;

	if (month) {
		const parsedDate = parse(month, "MMM yyyy", new Date());
		startDate = format(startOfMonth(parsedDate), "yyyy-MM-dd");
		endDate = format(endOfMonth(parsedDate), "yyyy-MM-dd");
		label = month;
	} else {
		startDate = dateFrom ?? "";
		endDate = dateTo ?? "";
		label = "selected period";
	}

	const { data, isLoading } = useContractInvoicesListQuery({
		collection_status: "pending",
		start_date: startDate,
		end_date: endDate,
		salesperson_id: salespersonId,
		per_page: 50,
		page,
	});

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const allData = await contractInvoiceService.list({
				collection_status: "pending",
				start_date: startDate,
				end_date: endDate,
				salesperson_id: salespersonId,
				per_page: 10000,
			});

			const allInvoices = (allData?.data ?? []) as InvoiceWithContract[];

			const headers = ["Project", "Invoice #", "Invoice Date", "Due Date", "Amount"];
			const rows = allInvoices.map((inv) => [
				inv.sales_contract?.project?.name ?? "—",
				inv.invoice_number,
				inv.invoice_date,
				inv.due_date,
				Number(inv.amount) || 0,
			]);

			const dateLabel = month ?? `${startDate}_to_${endDate}`;
			exportToXLSX({
				headers,
				rows,
				filename: `pending-invoices-${dateLabel}`,
			});
		} finally {
			setExporting(false);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
			</div>
		);
	}

	const invoices = (data?.data ?? []) as InvoiceWithContract[];
	const meta = data?.meta as Record<string, number> | undefined;
	const currentPage = meta?.current_page ?? 1;
	const lastPage = meta?.last_page ?? 1;

	const displayTotal = Number(data?.total_amount) || 0;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between bg-amber-50 p-3 rounded-lg">
				<div>
					<span className="text-sm text-gray-600">Total Pending</span>
					<span className="text-lg font-bold text-amber-600 ml-2">{formatCurrency(displayTotal)}</span>
				</div>
				{invoices.length > 0 && (
					<Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
						<IconFileSpreadsheet className="w-4 h-4 mr-1" />
						{exporting ? "Exporting..." : "Export"}
					</Button>
				)}
			</div>

			{invoices.length === 0 ? (
				<p className="text-center text-gray-500 py-4">No pending invoices for {label}</p>
			) : (
				<>
					<div className="max-h-[400px] overflow-y-auto">
						<table className="w-full text-sm">
							<thead className="sticky top-0 bg-white">
								<tr className="border-b text-left text-gray-500">
									<th className="py-2 pr-2">Project</th>
									<th className="py-2 pr-2">Invoice #</th>
									<th className="py-2 pr-2">Invoice Date</th>
									<th className="py-2 pr-2">Due Date</th>
									<th className="py-2 text-right">Amount</th>
								</tr>
							</thead>
							<tbody>
								{invoices.map((invoice) => (
									<tr key={invoice.id} className="border-b last:border-0 hover:bg-gray-50">
										<td className="py-2 pr-2">{invoice.sales_contract?.project?.name ?? "—"}</td>
										<td className="py-2 pr-2 font-medium">{invoice.invoice_number}</td>
										<td className="py-2 pr-2">{invoice.invoice_date}</td>
										<td className="py-2 pr-2">
											<span className={invoice.is_overdue ? "text-red-600 font-medium" : ""}>
												{invoice.due_date}
												{invoice.is_overdue && " (overdue)"}
											</span>
										</td>
										<td className="py-2 text-right">{formatCurrency(Number(invoice.amount) || 0)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{lastPage > 1 && (
						<div className="flex items-center justify-between pt-2">
							<span className="text-xs text-gray-500">
								Page {currentPage} of {lastPage}
							</span>
							<div className="flex gap-2">
								<Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>
									Previous
								</Button>
								<Button variant="outline" size="sm" disabled={currentPage >= lastPage} onClick={() => setPage((p) => p + 1)}>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};
